import './SourceInfoBox.scss';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'primereact/button';
import { TrendTag } from '../../../../projects/preview/components/trend-tag/TrendTag';
import { getTrendValue } from '../../../../../shared/utils/utils';
import PageTypeTag from '../../../../../shared/components/page-type-tag/PageTypeTag';

interface SourceInfoBoxProps {
  siteName: string;
  sourceDomain: string;
  cleanUrl: string;
  sourceHref: string;
  domainCategory: string | null;
  urlType?: string;
  usageCount?: number;
  usageChange?: number;
  promptCount?: number;
  promptCountChange?: number;
  firstSeen?: string;
  lastSeen?: string;
  /** Opens the stored-copy dialog; absent when the account lacks the SOURCE_PAGE_COPY addon. */
  onShowPageCopy?: () => void;
}

function getRelativeDate(dateStr: string, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.floor((today.getTime() - target.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return t('sourceToday');
  if (diffDays === 1) return t('sourceYesterday');
  if (diffDays <= 6) return t('sourceDaysAgo', { count: diffDays });
  if (diffDays <= 13) return t('sourceWeekAgo');
  if (diffDays <= 44) return t('sourceMonthAgo');
  return t('sourceOlderThanMonth');
}

function getLastSeenVariant(dateStr: string): 'active' | 'pending' | 'unavailable' {
  const date = new Date(dateStr);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.floor((today.getTime() - target.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'active';
  if (diffDays <= 7) return 'pending';
  return 'unavailable';
}

const SourceInfoBox: React.FC<SourceInfoBoxProps> = ({
  siteName,
  sourceDomain,
  cleanUrl,
  sourceHref,
  domainCategory,
  urlType,
  usageCount,
  usageChange,
  promptCount,
  promptCountChange,
  firstSeen,
  lastSeen,
  onShowPageCopy,
}) => {
  const { t } = useTranslation();

  const firstSeenLabel = useMemo(() => firstSeen ? getRelativeDate(firstSeen, t) : null, [firstSeen, t]);
  const lastSeenLabel = useMemo(() => lastSeen ? getRelativeDate(lastSeen, t) : null, [lastSeen, t]);
  const lastSeenVariant = useMemo(() => lastSeen ? getLastSeenVariant(lastSeen) : 'unavailable', [lastSeen]);

  return (
    <div className="source-info-box">
      <div className="source-info-box__top">
        <div className="source-info-box__left">
          <span
            className="source-info-box__icon"
            style={{ backgroundImage: `url("https://favicon.is/${sourceDomain}")` }}
          />
          <div className="source-info-box__details">
            <div className="source-info-box__name-row">
              <span className="source-info-box__name">{siteName}</span>
              {(domainCategory === 'COMPETITOR' || domainCategory === 'OWN') && (
                <PageTypeTag pageType={domainCategory} />
              )}
            </div>
            <div className="source-info-box__url-row">
              <span className="source-info-box__url text-ellipsis">{cleanUrl}</span>
            </div>
          </div>
        </div>
        <div className="source-info-box__right">
          {onShowPageCopy && (
            <Button
              type="button"
              className="btn btn--sm source-info-box__copy-btn"
              onClick={onShowPageCopy}
              label={t('savedPageCopy')}
            />
          )}
          <a
            href={sourceHref}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn--sm btn--primary source-info-box__view-btn"
          >
            <span className="ico--sm ico--external-link" />
            {t('viewPageContent')}
          </a>
        </div>
      </div>
      <div className="source-info-box__bottom">
        <div className="source-info-box__stat">
          <span className="source-info-box__stat-label">{t('sourceUrlType')}</span>
          <span className="source-info-box__stat-value">{urlType ? t(`pageType_${urlType}`) : '—'}</span>
        </div>
        <div className="source-info-box__stat">
          <span className="source-info-box__stat-label">{t('sourceUsage')}</span>
          <div className="source-info-box__stat-value">
            <span>{usageCount ?? '—'}</span>
            {usageChange !== undefined && (
              <TrendTag trendValue={getTrendValue(usageChange)} suffix="" />
            )}
          </div>
        </div>
        <div className="source-info-box__stat">
          <span className="source-info-box__stat-label">{t('sourceNoOfPrompts')}</span>
          <div className="source-info-box__stat-value">
            <span>{promptCount ?? '—'}</span>
            {promptCountChange !== undefined && (
              <TrendTag trendValue={getTrendValue(promptCountChange)} suffix="" />
            )}
          </div>
        </div>
        <div className="source-info-box__stat source-info-box__stat--wide">
          <span className="source-info-box__stat-label">{t('sourceLifecycle')}</span>
          {firstSeen && lastSeen && (
            <div className="source-info-box__lifecycle">
              <div className="source-info-box__lifecycle-point">
                <span className="source-info-box__lifecycle-title">{t('sourceFirstSeen')}</span>
                <span className="source-info-box__lifecycle-date">{firstSeenLabel}</span>
              </div>
              <div className="source-info-box__lifecycle-line">
                <div className="source-info-box__lifecycle-bar" />
                <div className={`status status--sm status--${lastSeenVariant}`}>
                  <span className="status__ring" />
                  <span className="status__dot" />
                </div>
              </div>
              <div className="source-info-box__lifecycle-point source-info-box__lifecycle-point--end">
                <span className="source-info-box__lifecycle-title">{t('sourceLastSeen')}</span>
                <span className={`source-info-box__lifecycle-date source-info-box__lifecycle-date--${lastSeenVariant}`}>
                  {lastSeenLabel}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SourceInfoBox;
